import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '@/components/landing/Navbar';
import Footer from '@/components/landing/Footer';
import { ArrowLeft, Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const ContactPage = () => {
  const { language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setIsLoading(false);
    setIsSubmitted(true);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="contact-page">
      <Navbar />
      
      <main className="vf-section">
        <div className="vf-container">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-600 hover:text-[#1e3a5f] mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            {language === 'es' ? 'Volver' : 'Back'}
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-8"
            >
              <div>
                <h1 className="vf-heading-1 text-[#1e3a5f] mb-4">
                  {language === 'es' ? 'Contacto' : 'Contact'}
                </h1>
                <p className="vf-body-large text-slate-600">
                  {language === 'es'
                    ? '¿Tienes preguntas? Estamos aquí para ayudarte. Responderemos en menos de 24 horas.'
                    : 'Have questions? We\'re here to help. We\'ll respond within 24 hours.'}
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1e3a5f] rounded-sm flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1e3a5f]">Email</h3>
                    <a 
                      href="mailto:info@vendfactura.com"
                      className="text-slate-600 hover:text-[#22c55e]"
                    >
                      info@vendfactura.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1e3a5f] rounded-sm flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1e3a5f]">
                      {language === 'es' ? 'Teléfono' : 'Phone'}
                    </h3>
                    <a 
                      href="tel:+34900000000"
                      className="text-slate-600 hover:text-[#22c55e]"
                    >
                      +34 900 000 000
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1e3a5f] rounded-sm flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1e3a5f]">
                      {language === 'es' ? 'Dirección' : 'Address'}
                    </h3>
                    <p className="text-slate-600">
                      Madrid, España
                    </p>
                  </div>
                </div>
              </div>

              {/* Support Hours */}
              <div className="bg-white border-2 border-slate-100 p-6">
                <h3 className="font-semibold text-[#1e3a5f] mb-4">
                  {language === 'es' ? 'Horario de Soporte' : 'Support Hours'}
                </h3>
                <div className="space-y-2 text-slate-600">
                  <div className="flex justify-between">
                    <span>{language === 'es' ? 'Lunes - Viernes' : 'Monday - Friday'}</span>
                    <span>9:00 - 18:00</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{language === 'es' ? 'Sábados' : 'Saturday'}</span>
                    <span>10:00 - 14:00</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{language === 'es' ? 'Domingos' : 'Sunday'}</span>
                    <span>{language === 'es' ? 'Cerrado' : 'Closed'}</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white border-2 border-[#1e3a5f] p-8 lg:p-10"
            >
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-[#22c55e]" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#1e3a5f] mb-4">
                    {language === 'es' ? '¡Mensaje Enviado!' : 'Message Sent!'}
                  </h3>
                  <p className="text-slate-600">
                    {language === 'es'
                      ? 'Gracias por contactarnos. Te responderemos pronto.'
                      : 'Thank you for contacting us. We\'ll respond soon.'}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <h2 className="text-2xl font-bold text-[#1e3a5f] mb-6">
                    {language === 'es' ? 'Envíanos un Mensaje' : 'Send us a Message'}
                  </h2>

                  <div>
                    <label className="block text-sm font-medium text-[#1e3a5f] mb-2">
                      {language === 'es' ? 'Nombre' : 'Name'}
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="vf-input w-full px-4 py-3"
                      placeholder={language === 'es' ? 'Tu nombre' : 'Your name'}
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#1e3a5f] mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="vf-input w-full px-4 py-3"
                      placeholder={language === 'es' ? 'tu@email.com' : 'your@email.com'}
                      data-testid="contact-email-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#1e3a5f] mb-2">
                      {language === 'es' ? 'Asunto' : 'Subject'}
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="vf-input w-full px-4 py-3"
                      placeholder={language === 'es' ? 'Asunto del mensaje' : 'Message subject'}
                      data-testid="contact-subject-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#1e3a5f] mb-2">
                      {language === 'es' ? 'Mensaje' : 'Message'}
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="vf-input w-full px-4 py-3 resize-none"
                      placeholder={language === 'es' ? 'Tu mensaje...' : 'Your message...'}
                      data-testid="contact-message-input"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="vf-btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="contact-submit-button"
                  >
                    {isLoading ? (
                      <span>{language === 'es' ? 'Enviando...' : 'Sending...'}</span>
                    ) : (
                      <>
                        <span>{language === 'es' ? 'Enviar Mensaje' : 'Send Message'}</span>
                        <Send className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
